import { Product } from '../../hooks/useProducts';
import { ProductDataResponse, RawProductData, ProductDataSource } from '../types/requests';
import { PRODUCT_ENDPOINTS } from './base';
import { ApiManager } from '../ApiManager';

/**
 * Product API endpoint methods
 */
export class ProductEndpoints {
  /**
   * Get product data from specified source
   */
  async getProductData(source: ProductDataSource = 'lilja'): Promise<Product[]> {
    const endpointKey = this.getEndpointKey(source);
    const endpoint = PRODUCT_ENDPOINTS[endpointKey];

    if (!endpoint) {
      throw new Error(`No endpoint found for product data source: ${source}`);
    }

    try {
      const response = await ApiManager.get<RawProductData>({ url: endpoint.url });
      return this.transformProductData(response.data);
    } catch (error) {
      throw new Error(`Failed to fetch product data: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Get single product by ID from API
   */
  async getProduct(
    productId: string,
    options: {
      country?: string;
      language?: string;
      experiments?: string;
      sessionId?: string;
    } = {}
  ): Promise<any> {
    // Use the remote API endpoint for product data
    // REACT_APP_PLAYMERCE_API_ROOT already includes /apps/playmerce
    const apiBase = process.env.REACT_APP_PLAYMERCE_API_ROOT || 'http://127.0.0.1:8081/apps/playmerce';
    const baseUrl = `${apiBase}/scroll-and-collect/product/${productId}`;

    // Build URL with parameters
    const url = new URL(baseUrl);

    if (options.country) {
      url.searchParams.append('country', options.country.toUpperCase());
    }
    if (options.language) {
      url.searchParams.append('language', options.language.toUpperCase());
    }
    if (options.experiments) {
      url.searchParams.append('experiments', options.experiments);
    }
    if (options.sessionId) {
      url.searchParams.append('session_id', options.sessionId);
    }

    try {
      const response = await ApiManager.get<any>({ url: url.toString() }, {});
      return response.data;
    } catch (error) {
      throw new Error(`Failed to fetch product: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Get all available product data sources
   */
  async getAllProductData(): Promise<Record<ProductDataSource, Product[]>> {
    const sources: ProductDataSource[] = ['lilja', 'laukkuni', 'default', '2'];
    const results: Record<string, Product[]> = {};

    await Promise.allSettled(
      sources.map(async (source) => {
        try {
          results[source] = await this.getProductData(source);
        } catch (error) {
          console.warn(`Failed to load product data for ${source}:`, error);
          results[source] = [];
        }
      })
    );

    return results as Record<ProductDataSource, Product[]>;
  }

  /**
   * Get product by ID from all sources
   */
  async getProductById(productId: string): Promise<Product | null> {
    const allData = await this.getAllProductData();

    for (const products of Object.values(allData)) {
      const product = products.find((p) => p.id === productId || p.id.toString().includes(productId));
      if (product) {
        return product;
      }
    }

    return null;
  }

  /**
   * Check availability status for multiple products
   */
  async checkProductsAvailability(productIds: string[], experiments?: string): Promise<Record<string, boolean>> {
    if (productIds.length === 0) {
      return {};
    }

    // Use the remote API endpoint for availability check
    const apiBase = process.env.REACT_APP_PLAYMERCE_API_ROOT || 'http://127.0.0.1:8081/apps/playmerce';
    const idsParam = productIds.join(',');
    const baseUrl = `${apiBase}/scroll-and-collect/product/${idsParam}/available`;

    // Build URL with experiments parameter if provided
    const url = new URL(baseUrl);
    if (experiments) {
      url.searchParams.append('experiments', experiments);
    }

    try {
      const response = await ApiManager.get<Record<string, boolean>>({ url: url.toString() });
      return response.data;
    } catch (error) {
      console.warn(`Failed to check product availability:`, error);
      // Return all as available on error to avoid breaking the UI
      return productIds.reduce((acc: Record<string, boolean>, id: string) => {
        acc[id] = true;
        return acc;
      }, {} as Record<string, boolean>);
    }
  }

  /**
   * Transform raw product data to match Product interface
   */
  private transformProductData(data: RawProductData): Product[] {
    if (!data?.data?.products?.edges) {
      console.warn('Unexpected data structure:', data);
      return [];
    }

    return data.data.products.edges.map((edge) => {
      const node = edge.node;

      // Extract price from variants
      const firstVariant = node.variants?.edges?.[0]?.node;
      const price = firstVariant?.price || '0';

      // Extract featured image from media
      const firstMedia = node.media?.edges?.[0]?.node;
      const featuredImage = firstMedia?.image
        ? {
            url: firstMedia.image.url,
            altText: node.title,
          }
        : null;

      return {
        ...node,
        priceRange: {
          minVariantPrice: {
            amount: price,
            currencyCode: 'EUR',
          },
        },
        featuredImage,
      };
    });
  }

  /**
   * Map source names to endpoint keys
   */
  private getEndpointKey(source: ProductDataSource): string {
    const mapping: Record<ProductDataSource, string> = {
      lilja: 'LILJA',
      laukkuni: 'LAUKKUNI',
      default: 'DEFAULT',
      '2': 'V2',
    };

    return mapping[source];
  }
}
